import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { ImageIcon, Upload, X, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FormData } from "../types";
import { ImageUploadModal, type UploadedImage } from "@/components/image-upload";
import { useImageUpload } from "@/hooks/useImageUpload";
import { useAuth } from "@/hooks/useAuth";

interface PhotosStepProps {
  formData: FormData;
  onPhotosChange: (photos: File[], urls: string[]) => void;
  onUploadedImagesChange?: (images: UploadedImage[]) => void;
}

const MIN_PHOTOS = 15;
const MAX_PHOTOS = 40;

const PhotosStep = ({ formData, onPhotosChange, onUploadedImagesChange }: PhotosStepProps) => {
  const { user, loading: authLoading } = useAuth();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [autoPromptShown, setAutoPromptShown] = useState(false);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const prevImagesRef = useRef<string>("");
  
  const {
    uploadedImages,
    isLoading,
    addUploadedImages,
    removeImage,
    getSignedUrl,
  } = useImageUpload(formData.uploadSessionId);

  // Memoize the callback to prevent infinite loops
  const stableOnUploadedImagesChange = useCallback(
    (images: UploadedImage[]) => {
      if (onUploadedImagesChange) {
        onUploadedImagesChange(images);
      }
    },
    [onUploadedImagesChange]
  );

  // Sync uploaded images with form data - only when images actually change
  useEffect(() => {
    const imagesKey = uploadedImages.map((img) => img.id).join(",");
    if (imagesKey !== prevImagesRef.current) {
      prevImagesRef.current = imagesKey;
      stableOnUploadedImagesChange(uploadedImages);
    }
  }, [uploadedImages, stableOnUploadedImagesChange]);

  // Auto-show upload modal (which shows account prompt) for unauthenticated users
  useEffect(() => {
    if (!authLoading && !user && !autoPromptShown && formData.uploadPhotosNow !== "later") {
      setShowUploadModal(true);
      setAutoPromptShown(true);
    }
  }, [authLoading, user, autoPromptShown, formData.uploadPhotosNow]);

  // Load signed URLs for uploaded images
  useEffect(() => {
    const loadUrls = async () => {
      for (const img of uploadedImages) {
        if (!imageUrls[img.id]) {
          try {
            const url = await getSignedUrl(img.file_path);
            setImageUrls((prev) => ({ ...prev, [img.id]: url }));
          } catch (err) {
            console.error("Failed to get signed URL:", err);
          }
        }
      }
    };
    loadUrls();
  }, [uploadedImages, getSignedUrl]);

  const handleUploadComplete = (newImages: UploadedImage[]) => {
    addUploadedImages(newImages);
    setShowUploadModal(false);
  };

  const handleRemoveImage = async (imageId: string) => {
    await removeImage(imageId);
    setImageUrls((prev) => {
      const updated = { ...prev };
      delete updated[imageId];
      return updated;
    });
  };

  if (formData.uploadPhotosNow === "later") {
    return (
      <motion.div
        key="photos"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="space-y-6"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <ImageIcon className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
            Upload Photos Later
          </h2>
        </div>

        <div className="bg-accent/20 border border-accent rounded-xl p-6">
          <p className="text-foreground mb-4">
            You can upload your photos anytime from your account after purchase.
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            <strong>Important:</strong> If photos are not uploaded at the time of purchase, 
            or if uploaded photos do not meet the minimum size requirement, video creation may be delayed.
          </p>
          <p className="text-sm text-muted-foreground">
            In your account, you will be able to:
          </p>
          <ul className="text-sm text-muted-foreground list-disc list-inside mt-2">
            <li>View uploaded photos</li>
            <li>Add or replace photos before production begins</li>
          </ul>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      key="photos"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <ImageIcon className="w-8 h-8 text-primary" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
          Upload Your Photos
        </h2>
        <p className="text-muted-foreground">
          Add {MIN_PHOTOS}-{MAX_PHOTOS} photos for your slideshow video.
        </p>
      </div>

      <div className="bg-muted/50 rounded-xl p-4 mb-2">
        <h4 className="font-medium text-foreground mb-2">📋 Image Requirements</h4>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>• Upload {MIN_PHOTOS} to {MAX_PHOTOS} photos</li>
          <li>• Each photo must be between <strong>500 KB and 10 MB</strong></li>
          <li>• Minimum resolution: <strong>800×800px</strong></li>
          <li>• Accepted formats: JPG, PNG, HEIC</li>
        </ul>
      </div>

      <div className="bg-accent/10 border border-accent/30 rounded-xl p-4 mb-6">
        <p className="text-sm text-muted-foreground">
          <strong>📱 Tip:</strong> Most cell phone photos are 2–5 MB and work perfectly. 
          If a photo is too large, try sending it to yourself via email or messaging app first — 
          this usually compresses it to an ideal size.
        </p>
      </div>

      <div className="space-y-4">
        <Label>Your Photos ({uploadedImages.length}/{MAX_PHOTOS})</Label>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {uploadedImages.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-4">
                {uploadedImages.map((image) => (
                  <div key={image.id} className="relative aspect-square rounded-lg overflow-hidden group border-2 border-primary/30">
                    {imageUrls[image.id] ? (
                      <img
                        src={imageUrls[image.id]}
                        alt={image.file_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(image.id)}
                      className="absolute top-1 right-1 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                      {image.width}×{image.height}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              className="w-full h-24 border-dashed"
              onClick={() => setShowUploadModal(true)}
              disabled={uploadedImages.length >= MAX_PHOTOS}
            >
              <Upload className="w-6 h-6 mr-2" />
              {uploadedImages.length === 0
                ? "Click to upload photos"
                : `Add more photos (${MAX_PHOTOS - uploadedImages.length} remaining)`}
            </Button>
          </>
        )}

        {uploadedImages.length > 0 && uploadedImages.length < MIN_PHOTOS && (
          <p className="text-sm text-amber-600">
            Please upload at least {MIN_PHOTOS - uploadedImages.length} more photo(s).
          </p>
        )}

        {uploadedImages.length >= MIN_PHOTOS && (
          <p className="text-sm text-primary">
            ✓ Minimum requirement met. You can add up to {MAX_PHOTOS - uploadedImages.length} more photos.
          </p>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Your photos are used only to create your slideshow video and are never shared.
      </p>

      <ImageUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        uploadSessionId={formData.uploadSessionId}
        onUploadComplete={handleUploadComplete}
        existingImages={uploadedImages}
      />
    </motion.div>
  );
};

export default PhotosStep;
